import type { FC } from 'react';
import type { HowToStartProps } from './types';

import { useMemo } from 'react';
import styled from 'styled-components';

import {
  COLOR_GRAY_13,
  COLOR_BLUE_6,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  shade,
  COLOR_WHITE,
  generateAccessKey
} from 'shared/helpers/index';
import { useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Wrapper } from '../Common';
import { renderSVG } from './helpers';
import useIsEmpty from 'shared/hooks/useIsEmpty';

const Container = styled.div`
  padding: 72px 0 80px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 52px 0;
  `}
`;

const StyledWrapper = styled(Wrapper)`
  display: flex;
  justify-content: space-between;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
  `}
`;

const TitleWrapper = styled.div`
  width: 30.5%;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    margin-bottom: 30px;
  `}
`;

const Title = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
  margin-bottom: 24px;
  position: relative;
  min-height: inherit;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 18px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 10px;
  `}
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    min-height: 115px;
    &:not(:focus):before {
      content: attr(data-placeholder);
      width: 100%;
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  color: ${COLOR_GRAY_13};
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  line-height: 1.35em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Benefits = styled.div`
  display: flex;
  justify-content: space-between;
  width: 64%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 67%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    flex-direction: column;
  `}
`;

const Item = styled.div`
  width: 31%;
  padding: 29px 20px 32px;
  background: #fff;
  border-radius: 10px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 22px 14px 22px;

  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 12px 18px;
    width: 100%;
    display: flex;

    &:not(:last-child) {
      margin-bottom: 15px;
    }
  `}
`;

const ItemInner = styled.div`
  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    display: flex;
    align-items: center;
  `}
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-bottom: 17px;
  flex-shrink: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.12);

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 70px;
    height: 70px;
    margin-bottom: 12px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin: 0;
    margin-right: 14px;
  `}
`;

const Icon = styled.svg`
  width: 39px;
  height: 39px;
  object-fit: contain;
  position: relative;
  z-index: 3;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 32px;
    height: 32px;
  `}
`;

const IconBg = styled.div`
  border-radius: 50%;
  width: 100%;
  height: 100%;
  position: absolute;
  background: #000;
  opacity: 0.24;
  z-index: 1;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: 65%;
  max-width: 100%;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  font-weight: 500;
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  margin-bottom: 10px;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 5px;
  `}
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    min-height: 50%;
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      width: 100%;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const DescriptionBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.35em;
  color: ${COLOR_GRAY_13};
  position: relative;
  min-height: 50%;
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      width: 100%;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

export const V1: FC<HowToStartProps> = (props) => {
  const {
    title = defaultWebValue,
    content = defaultWebValue,
    benefits = [],
    textColor,
    backgroundColor = COLOR_BLUE_6,
    meta,
    hidden,
    customBackgroundColor,
    oppositeBackgroundColor,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { isDashboard } = useSales();

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor ? customBackgroundColor : shade(backgroundColor, 0.7);
  }, [backgroundColor, customBackgroundColor]);

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('ht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('hc', order), [order]);
  const benefitsAccessKey = useMemo(() => generateAccessKey('hben', order), [order]);
  const skipLinkId = useMemo(
    () => `skip-to-howtostart-${meta['accessorKey'] || 'howtostart'}`,
    [meta]
  );

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `How to start edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  return (
    <Container
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: resolvedBackgroundColor
      }}
      aria-label="How to start section with step-by-step guide"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to how to start content</a>
      </span>

      <StyledWrapper id={skipLinkId}>
        <TitleWrapper id="HowToStartSection">
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim() ? 'How to start section title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this section title`,
                  tabIndex: 0
                })}
          />

          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim()
                      ? 'How to start section description'
                      : undefined,
                  'aria-describedby': title.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this section description`,
                  tabIndex: 0
                })}
          />
        </TitleWrapper>

        <Benefits
          role="region"
          aria-label={`Step-by-step guide with ${benefits.length} steps`}
          {...(!isDashboard && {
            accessKey: benefitsAccessKey,
            title: `Press Alt+${benefitsAccessKey} to focus the steps section`,
            tabIndex: 0
          })}>
          {benefits.map(({ title, content }, index) => {
            const isTitleEmpty = checkTitleBenefits(index);
            const isContentEmpty = checkContentBenefits(index);
            const stepAccessKey = generateAccessKey('hstep', index);

            return (
              <Item
                style={{
                  background: oppositeBackgroundColor || backgroundColor,
                  display: isTitleEmpty && isContentEmpty && !websiteAssembler ? 'none' : 'block'
                }}
                key={index}
                role="article"
                aria-label={`Step ${index + 1}: ${title?.val || 'Process step'}`}
                {...(!isDashboard && {
                  accessKey: stepAccessKey,
                  title: `Press Alt+${stepAccessKey} to focus step ${index + 1}`,
                  tabIndex: 0
                })}>
                <ItemInner>
                  <IconWrapper aria-hidden="true" role="presentation">
                    <Icon
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 34 34"
                      style={{
                        fill: filterBackgroundColor(
                          oppositeBackgroundColor
                            ? oppositeBackgroundColor || resolvedBackgroundColor
                            : backgroundColor,
                          textColor
                        )
                      }}
                      aria-hidden="true"
                      focusable="false">
                      {renderSVG(index)}
                    </Icon>
                    <IconBg
                      style={{ background: COLOR_WHITE }}
                      aria-hidden="true"
                      role="presentation"
                    />
                  </IconWrapper>

                  <TextWrapper>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{
                        color: filterBackgroundColor(
                          oppositeBackgroundColor
                            ? oppositeBackgroundColor || resolvedBackgroundColor
                            : backgroundColor,
                          textColor
                        )
                      }}
                      isEmpty={isTitleEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'title',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !title?.val?.trim()
                              ? `Step ${index + 1} title`
                              : undefined
                          })}
                    />

                    <DescriptionBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{
                        color: filterBackgroundColor(
                          oppositeBackgroundColor
                            ? oppositeBackgroundColor || resolvedBackgroundColor
                            : backgroundColor,
                          textColor
                        )
                      }}
                      isEmpty={isContentEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'content',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !content?.val?.trim()
                              ? `Step ${index + 1} description`
                              : undefined,
                            'aria-describedby': title?.id
                          })}
                    />
                  </TextWrapper>
                </ItemInner>
              </Item>
            );
          })}
        </Benefits>
      </StyledWrapper>
    </Container>
  );
};

V1.displayName = 'HowToStartV1';
